import { KindergartenDto, LocationDto } from '../../common/interfaces/kindergarten.interface';
import React, { useMemo } from 'react';
import { Box, Checkbox, CircularProgress, Divider, Link, Typography } from '@mui/material';
import { GroupBox, LocationWrapper } from './GenericLocationGroupSelector.styles';
import { useNavigate } from 'react-router-dom';
import { KindergartenDetailsTabs } from '../../common/enums/common.enum';
import { useFormikContext } from 'formik';
import { FormValues } from '../../pages/KindergartenDetails/components/EventTab/CreateConfiguration/CreateConfiguration';

interface LocationGroupSelectorProps {
  locations?: LocationDto[];
  kindergartens?: KindergartenDto[];
  kindergartenId?: string;
  selectedGroups?: string[];
  onChange?: (selectedGroups: string[]) => void;
  disabled?: boolean;
  exclusive?: boolean;
  isLoading?: boolean;
  withKindergarten?: boolean;
  isPost?: boolean;
  useFormik?: boolean;
}

const GenericLocationGroupsSelector: React.FC<LocationGroupSelectorProps> = ({
  locations,
  kindergartens,
  kindergartenId,
  selectedGroups: externalSelectedGroups = [],
  onChange,
  disabled,
  exclusive = false,
  isLoading = false,
  withKindergarten = false,
  isPost,
  useFormik = false
}) => {
  const navigate = useNavigate();

  const formik = useFormikContext<FormValues>();
  const selectedGroups: string[] = useFormik ? (formik?.values?.selectedGroups ?? []) : externalSelectedGroups;
  const error = useFormik ? formik?.errors?.selectedGroups : undefined;
  const touched = useFormik ? formik?.touched?.selectedGroups : undefined;

  const handleChange = (updated: string[]) => {
    if (formik) {
      formik.setFieldValue('selectedGroups', updated);
    } else {
      onChange?.(updated);
    }
  };
  const toggleGroup = (groupId: string) => {
    if (disabled) return;
    const updated = selectedGroups.includes(groupId)
      ? selectedGroups.filter(id => id !== groupId)
      : exclusive
        ? [groupId]
        : [...selectedGroups, groupId];
    handleChange(updated);
  };

  const getLocationGroupIds = (loc: LocationDto) => loc.groups?.map(g => g.id!) || [];

  const getKindergartenGroupIds = (kg: KindergartenDto) => kg.locations?.flatMap(loc => loc.groups?.map(g => g.id!) || []) || [];

  const areAllSelected = (ids: string[]) => ids.every(id => selectedGroups.includes(id));
  const isSomeSelected = (ids: string[]) => ids.some(id => selectedGroups.includes(id));

  const toggleLocation = (loc: LocationDto) => {
    const ids = getLocationGroupIds(loc);
    const allSelected = areAllSelected(ids);
    const someSelected = isSomeSelected(ids);

    let updated: string[];

    if (allSelected || someSelected) {
      updated = selectedGroups.filter(id => !ids.includes(id));
    } else {
      updated = Array.from(new Set([...selectedGroups, ...ids]));
    }

    handleChange(updated);
  };

  const toggleKindergarten = (kg: KindergartenDto) => {
    const ids = getKindergartenGroupIds(kg);
    const allSelected = areAllSelected(ids);
    const someSelected = isSomeSelected(ids);
    let updated: string[];

    if (allSelected) {
      updated = selectedGroups.filter(id => !ids.includes(id));
    } else if (someSelected) {
      updated = selectedGroups.filter(id => !ids.includes(id));
    } else {
      updated = Array.from(new Set([...selectedGroups, ...ids]));
    }
    handleChange(updated);
  };

  const kindergartensWithLocations = useMemo(() => {
    return kindergartens?.filter(item => item.locations && item.locations.length);
  }, [kindergartens]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
          <CircularProgress size={24} />
        </Box>
      ) : withKindergarten && kindergartensWithLocations?.length ? (
        kindergartensWithLocations.map(kg => {
          const kgGroupIds = getKindergartenGroupIds(kg);
          const kgAllSelected = areAllSelected(kgGroupIds);
          const kgSomeSelected = !kgAllSelected && isSomeSelected(kgGroupIds);
          return (
            <Box key={kg.id} sx={{ border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
              <Box
                sx={{
                  backgroundColor: '#F5F5FE',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h5">{kg.name}</Typography>
                <Checkbox
                  checked={kgAllSelected}
                  indeterminate={kgSomeSelected}
                  onChange={() => toggleKindergarten(kg)}
                  disabled={disabled}
                />
              </Box>

              {kg.locations?.map((loc, idx) => {
                const locGroupIds = getLocationGroupIds(loc);
                const locAllSelected = areAllSelected(locGroupIds);
                const locSomeSelected = !locAllSelected && isSomeSelected(locGroupIds);

                return (
                  <React.Fragment key={loc.id}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontSize: 14 }}>
                          {loc.name}
                        </Typography>
                        <Checkbox
                          checked={locAllSelected}
                          indeterminate={locSomeSelected}
                          onChange={() => toggleLocation(loc)}
                          disabled={disabled}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: 1 }}>
                        {loc.groups?.map(group => (
                          <GroupBox
                            key={group.id}
                            sx={{
                              backgroundColor: selectedGroups.includes(group.id!) ? '#F5F5FE' : '#FFF',
                              cursor: disabled ? 'default' : 'pointer',
                              opacity: disabled ? 0.6 : 1
                            }}
                            onClick={() => toggleGroup(group.id!)}
                          >
                            <Typography variant="body2" sx={{ color: selectedGroups.includes(group.id!) ? '#483359' : '' }}>
                              {group.name}
                            </Typography>
                          </GroupBox>
                        ))}
                      </Box>
                    </Box>
                    {idx < (kg.locations?.length ?? 0) - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </Box>
          );
        })
      ) : locations && locations.length > 0 ? (
        locations.map(loc => (
          <LocationWrapper key={loc.id}>
            <Typography variant="body2" sx={{ color: '#483359' }}>
              {loc.name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {loc.groups?.map(group => (
                <GroupBox
                  key={group.id}
                  sx={{
                    backgroundColor: selectedGroups.includes(group.id!) ? '#F5F5FE' : '#FFF',
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.6 : 1
                  }}
                  onClick={() => toggleGroup(group.id!)}
                >
                  <Typography variant="body2" sx={{ color: selectedGroups.includes(group.id!) ? '#483359' : '' }}>
                    {group.name}
                  </Typography>
                </GroupBox>
              ))}
            </Box>
          </LocationWrapper>
        ))
      ) : isPost ? (
        <Typography variant="body1">Please create a location and group first, and then assign the post to it.</Typography>
      ) : (
        <Typography variant="body1">
          Please{' '}
          <Link
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.LocationsAndGroups}`)}
            sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
          >
            create a location &amp; group
          </Link>{' '}
          first, and then assign the user to it.
        </Typography>
      )}
      {touched && error && <Typography color="error"> {error as string}</Typography>}
    </Box>
  );
};
export default GenericLocationGroupsSelector;
